import { z } from 'zod';
import { uuidSchema, pageCursorSchema } from './common.js';
import { orgContactFieldsSchema, orgPreferenceFieldsSchema, orgProfileSchema } from './client-profile.js';

export const buyerDetailSchema = z.object({
  buyerOrgId: uuidSchema,
  legalName: z.string(),
  shortName: z.string().nullable(),
  contactEmail: z.string().nullable(),
  contactPhone: z.string().nullable(),
  website: z.string().nullable(),
  description: z.string().nullable(),
  address: orgContactFieldsSchema.shape.address,
  poolNote: z.string().nullable(),
  prefAssetClasses: orgProfileSchema.shape.prefAssetClasses,
  prefManufacturers: orgProfileSchema.shape.prefManufacturers,
  prefRegions: orgProfileSchema.shape.prefRegions,
  prefShippingMethods: orgProfileSchema.shape.prefShippingMethods,
  prefDcCapability: orgProfileSchema.shape.prefDcCapability,
  prefServices: orgProfileSchema.shape.prefServices,
  isActive: z.boolean(),
  isTest: z.boolean(),
  createdAt: z.string(),
  transactionCount: z.number(),
  /** RUF-538: profileCompleteness() predicate over PROFILE_COMPLETENESS_COLUMNS */
  complete: z.boolean(),
  missingFields: z.array(z.string()),
});

export type BuyerDetail = z.infer<typeof buyerDetailSchema>;

export const buyerDetailResponseSchema = z.object({
  data: buyerDetailSchema,
});
export type BuyerDetailResponse = z.infer<typeof buyerDetailResponseSchema>;

export const buyerListResponseSchema = z.object({
  data: z.array(buyerDetailSchema),
  nextCursor: pageCursorSchema.nullable(),
});
export type BuyerListResponse = z.infer<typeof buyerListResponseSchema>;

export const buyerCreateSchema = z.object({
  legalName: z.string().min(1).optional(),
  shortName: z.string().optional(),
}).merge(orgContactFieldsSchema).merge(orgPreferenceFieldsSchema);
export type BuyerCreate = z.infer<typeof buyerCreateSchema>;

export const buyerUpdateSchema = buyerCreateSchema.partial();
export type BuyerUpdate = z.infer<typeof buyerUpdateSchema>;

/**
 * Lightweight active-buyer-profile list item (RUF-406).
 *
 * Powers the ops transactions buyer filter + Record-transaction picker via
 * `GET /api/customers/buyers` — mirrors the shape/semantics of
 * `sellerListResponseSchema` (active-only, no pagination). Replaces the
 * deleted `GET /api/buyers` (buyers.ts).
 */
export const buyerFilterItemSchema = z.object({
  buyerOrgId: uuidSchema,
  legalName: z.string(),
  shortName: z.string().nullable(),
  isTest: z.boolean(),
});
export type BuyerFilterItem = z.infer<typeof buyerFilterItemSchema>;

export const buyerFilterListResponseSchema = z.object({
  data: z.array(buyerFilterItemSchema),
});
export type BuyerFilterListResponse = z.infer<typeof buyerFilterListResponseSchema>;
